<i18n
  src="@/locales/views/agro_exporter/reporting/process/list_reports.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="ae_reporting_process_list_processes_path"
    :params="{
      agro_exporter_id: agro_exporter_id
    }"
  ></go-back-link>
  <bread-crumbs
    :labels="[module_name, agro_exporter_process?.name]"
  ></bread-crumbs>

  <template v-if="load_data_general">
    <tabs-container v-model="option_tabs"></tabs-container>

    <div class="mt-6" v-show="this.option_tabs[0].current">
      <h1 class="page-title">
        <i :class="this.option_tabs[0].icon"></i
        ><span class="space-icon"></span>
        {{ $t("labels.process_reports") }}
        <span class="space-icon"></span>
        <i
          v-if="show_report_process"
          class="fa-solid fa-chevron-right fa-xs"
        ></i>
        <span class="space-icon"></span>
        <span class="text-base">{{ report_for_process_selected?.name }}</span>
      </h1>

      <div class="mt-8 flex flex-col" v-show="!show_report_process">
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul role="list" class="divide-y divide-gray-200">
            <li v-for="report in reports_for_process" :key="report.id">
              <div
                class="block hover:bg-gray-50 cursor-pointer"
                @click.prevent="showReportProcess(report)"
              >
                <div class="px-4 py-4 sm:px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm font-medium text-gray-900 truncate">
                      {{ report?.name }}
                    </p>
                    <div class="ml-2 flex-shrink-0 flex">
                      <a class="link invisible sm:visible">
                        <i class="fa-solid fa-eye"></i>
                        <span class="space-icon"></span>
                        {{ $t("labels.show_link") }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div v-show="show_report_process">
        <div class="mt-8 flex flex-col">
          <div class="table-first-container">
            <div class="table-second-container">
              <div class="table-third-container">
                <table class="table">
                  <thead class="table-header">
                    <tr>
                      <th class="table-header-cell"></th>
                      <th
                        v-for="(label, col) in summaryCols(report_process)"
                        :key="col"
                        class="table-header-cell"
                      >
                        {{ label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="table-body">
                    <tr
                      class="table-row"
                      v-for="(row, idx) in summaryRows(report_process)"
                      :key="idx"
                    >
                      <td class="table-cell">{{ row.name }}</td>
                      <td
                        v-for="(label, col) in summaryCols(report_process)"
                        :key="col"
                        class="table-cell"
                      >
                        {{ summaryValue(col, row) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-8 flex flex-col">
          <template v-if="!loading_report">
            <div
              class="bg-white rounded-md flex"
              v-if="
                seriesData(report_process).length > 0 &&
                chartOptions(report_process)
              "
            >
              <div class="rounded-md px-6 flex-1 pt-4">
                <apexchart
                  height="500"
                  :options="chartOptions(report_process)"
                  :series="seriesData(report_process)"
                ></apexchart>
              </div>
            </div>
          </template>
          <loading-message v-else />
        </div>
      </div>
    </div>

    <div class="mt-6" v-show="this.option_tabs[1].current">
      <h1 class="page-title items-center">
        <i :class="this.option_tabs[1].icon"></i
        ><span class="space-icon"></span>
        {{ $t("labels.containers_reports") }}
        <span class="space-icon"></span>
        <i
          v-if="show_report_container"
          class="fa-solid fa-chevron-right fa-xs"
        ></i>
        <span class="space-icon"></span>
        <span class="text-base">{{ report_for_container_selected?.name }}</span>
      </h1>

      <div class="mt-8 flex flex-row">
        <div class="panel-plain-label">
          <badge-default
            :label="
              containers.length + ' ' + $t('labels.total_number_containers')
            "
          />
        </div>
      </div>

      <div class="mt-8 flex flex-col" v-show="!show_report_container">
        <div class="table-first-container">
          <div class="table-second-container">
            <div class="table-third-container">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.container.week") }}
                    </th>
                    <th scope="col" class="table-header-cell text-center">
                      {{ $t("model.container.container_code") }}
                    </th>
                    <th scope="col" class="table-header-cell text-center">
                      {{ $t("model.container.packing_house") }}
                    </th>
                    <th scope="col" class="table-header-cell text-center">
                      {{ $t("model.container.customer") }}
                    </th>
                    <th scope="col" class="table-header-cell"></th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr
                    class="table-row"
                    v-for="container in containers"
                    :key="container.id"
                  >
                    <td class="table-cell">{{ container.week }}</td>
                    <td class="table-cell-main text-center">
                      {{ container.container_code }}
                    </td>
                    <td class="table-cell text-center">
                      {{ container.packing_house?.name }}
                    </td>
                    <td class="table-cell text-center">
                      {{ container.customer?.name }}
                    </td>
                    <td class="table-cell-action">
                      <template
                        v-for="report in reports_for_container"
                        :key="report.id"
                      >
                        <a
                          class="link"
                          @click.prevent="
                            showReportContainer(container, report)
                          "
                        >
                          <i class="fa-solid fa-eye"></i>
                          <span class="space-icon"></span>
                          {{ report?.name }}
                        </a>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8 flex bg-white rounded-md" v-show="show_report_container">
        <div class="flex-none w-min">
          <table class="hidden sm:block divide-y divide-gray-200">
            <thead class="text-gray-600">
              <tr class="border-r text-sm">
                <th class="py-2 px-1 text-center" scope="col">
                  {{ $t("model.container.week_abbrev") }}
                </th>
                <th class="py-2 px-1 justify-center text-center" scope="col">
                  {{ $t("model.container._self") }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                :class="[
                  ' text-center',
                  container_selected?.container_code == container.container_code
                    ? 'text-lime-600'
                    : 'text-gray-500 border-r'
                ]"
                v-for="container in containers"
                :key="container.id"
              >
                <td class="p-2 text-sm font-medium">{{ container.week }}</td>
                <td class="p-2 justify-center">
                  <button
                    class="items-center text-sm font-medium"
                    @click.prevent="changeContainer(container)"
                  >
                    {{ container.container_code }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="grid grid-cols-1 rounded-md px-6">
          <div v-if="!loading_report">
            <container-info
              v-if="container_selected"
              class="mt-6"
              :container="container_selected"
              :show_container_code="true"
              :show_panel="false"
            ></container-info>

            <div class="m-8">
              <div class="table-first-container">
                <div class="table-second-container">
                  <div class="table-third-container">
                    <table class="table">
                      <thead class="table-header">
                        <tr>
                          <th class="table-header-cell"></th>
                          <th
                            v-for="(label, col) in summaryCols(
                              report_container
                            )"
                            :key="col"
                            class="table-header-cell"
                          >
                            {{ label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="table-body">
                        <tr
                          class="table-row"
                          v-for="(row, idx) in summaryRows(report_container)"
                          :key="idx"
                        >
                          <td class="table-cell">{{ row.name }}</td>
                          <td
                            v-for="(label, col) in summaryCols(
                              report_container
                            )"
                            :key="col"
                          >
                            {{ summaryValue(col, row) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-white mt-8">
              <apexchart
                v-if="
                  seriesData(report_container).length > 0 &&
                  chartOptions(report_container)
                "
                height="400"
                :options="chartOptions(report_container)"
                :series="seriesData(report_container)"
              ></apexchart>
            </div>
          </div>
          <loading-message v-else />
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import VueApexCharts from "vue3-apexcharts";

import Requester from "@/views/agro_exporter/requesters/reporting/process/reports.js";
import BreadCrumbs from "@/components/views/layout/BreadCrumbs.vue";
import TabsContainer from "@/components/views/layout/TabsContainer.vue";
import ContainerInfo from "@/components/views/agro_exporter/reporting/ContainerInfo.vue";

export default {
  components: {
    BreadCrumbs,
    TabsContainer,
    ContainerInfo,
    apexchart: VueApexCharts
  },
  name: "AEQualityReportsProcessListProcesses",
  props: ["agro_exporter_id", "agro_exporter_process_id"],
  data() {
    return {
      load_data_general: false,
      loading_report: false,
      agro_exporter_process: {},
      containers: {},
      report_container: null,
      report_process: null,
      show_report_container: false,
      show_report_process: false,
      container_selected: null,
      report_for_container_selected: null,
      report_for_process_selected: null,
      option_tabs: [
        {
          name: this.$t("labels.process_reports"),
          icon: "fa-solid fa-chart-gantt",
          current: true
        },
        {
          name: this.$t("labels.containers_reports"),
          icon: "fa-solid fa-chart-column",
          current: false
        }
      ]
    };
  },
  computed: {
    reports_for_container() {
      return (this.agro_exporter_process.agro_exporter_reports || []).filter(
        report => report.for_container
      );
    },
    reports_for_process() {
      return (this.agro_exporter_process.agro_exporter_reports || []).filter(
        report => report.for_process
      );
    }
  },
  methods: {
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainers: async function () {
      let response = await Requester.fetchContainers(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.containers = response.data;
      this.load_data_general = true;
    },
    fetchReportProcess: async function () {
      this.loading_report = true;
      this.report_process = null;
      let response = await Requester.fetchReportProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id,
        this.report_for_process_selected.id
      );
      this.report_process = response.data;
      this.loading_report = false;
    },
    fetchReportContainer: async function () {
      this.loading_report = true;
      this.report_container = null;
      let response = await Requester.fetchReportContainer(
        this.agro_exporter_id,
        this.agro_exporter_process_id,
        this.report_for_container_selected.id,
        this.container_selected.id
      );
      this.report_container = response.data;
      this.loading_report = false;
    },
    showReportProcess: async function (report) {
      this.report_for_process_selected = report;
      this.show_report_process = true;
    },
    showReportContainer: function (container, report) {
      this.report_for_container_selected = report;
      this.container_selected = container;
      this.show_report_container = true;
    },
    changeContainer: function (container) {
      if (this.show_report_container) this.container_selected = container;
    },
    seriesData(report) {
      if (report == null) return [];
      return report.series.map(serie => {
        return {
          name: serie?.name,
          type: serie.graph_type,
          data: serie.data
        };
      });
    },
    chartOptions(report) {
      if (report == null) return null;
      return {
        chart: {
          height: 350,
          stacked: report.stacked
        },
        legend: {
          position: "top"
        },
        stroke: {
          width: report.series.map(serie => serie.stroke)
        },
        dataLabels: {
          enabled: false
        },
        labels: report.xaxis.labels,
        yaxis: this.yAxis(report),
        colors: this.colors(report),
        annotations: {
          yaxis: this.yAnnotations(report)
        },
        xaxis: this.xAxis(report)
      };
    },
    colors(report) {
      if (report == null) return [];
      let colors = report.colors;
      return report.series.map((serie, i) => {
        return serie.color || colors[i];
      });
    },
    xAxis(report) {
      if (report == null) return {};
      return {
        title: {
          text: report.xaxis.title,
          position: 'bottom'
        },
        labels: {
          rotate: -45,
          rotateAlways: true
        }
      };
    },
    yAxis(report) {
      if (report == null) return [];
      return report.series.map(serie => {
        let axis = {
          title: {
            text: serie.label
          },
          opposite: serie.yaxis.opposite,
          show: serie.yaxis.show,
          min: serie.yaxis.min,
          labels: {
            formatter: function (val) {
              if (val == null) return val;
              else if (Number.isSafeInteger(val)) return val.toFixed(0);
              else return val.toFixed(2);
            }
          }
        };
        if (serie.graph_type == "column") {
          axis["max"] = serie.yaxis.max || this.maxValue(report);
        } else if (serie.graph_type == "line") {
          axis["max"] = serie.yaxis.max || null;
        }
        if (serie.yaxis.steps_number) {
          axis["tickAmount"] = serie.yaxis.steps_number;
        }
        return axis;
      });
    },
    maxValue(report) {
      if (report == null) return 0;
      let serie_data = report.series
        .filter(serie => serie.graph_type == "column")
        .map(serie => serie.data);

      let max_value = 0;

      for (let index = 0; index < serie_data[0].length; index++) {
        let new_max = serie_data.reduce((acc, curr) => {
          return acc + curr[index];
        }, 0);
        if (new_max > max_value) max_value = new_max;
      }
      return max_value;
    },
    yAnnotations(report) {
      if (report == null) return [];
      return report.y_annotations.map(annotation => {
        return {
          y: annotation.y,
          strokeDashArray: 0,
          borderColor: annotation.color,
          yAxisIndex: annotation.serieIndex,
          label: {
            borderColor: annotation.color,
            style: {
              color: annotation.color
            },
            text: annotation.label,
            offsetY: 5,
            offsetX: 25
          }
        };
      });
    },
    summaryCols(report) {
      if (report == null) return [];
      return report.xaxis.labels;
    },
    summaryRows(report) {
      if (report == null) return [];
      return report.series.filter(serie => serie.graph_type == "column");
    },
    summaryValue(col, row) {
      return row.data[col];
    },
    reportContainerSummary(report) {
      const total = this.calcTotal(report);
      if (report == null) return [];
      return report.series
        .filter(serie => serie.graph_type == "column")
        .map(serie => {
          let total_serie = serie.data.reduce((sum, val) => sum + val, 0);
          return {
            label: serie.name,
            total_serie: total_serie,
            percentage: this.numberWithPrecision((total_serie / total) * 100)
          };
        });
    },
    calcTotal(report) {
      if (report == null) return;

      return report.series
        .filter(serie => serie.graph_type == "column")
        .map(serie => serie.data)
        .flat()
        .reduce((sum, val) => sum + val, 0);
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.fetchAgroExporterProcess();
        this.fetchContainers();
      })
    );
  },
  watch: {
    option_tabs: {
      handler: function () {
        this.show_report_container = false;
        this.show_report_process = false;
        this.report_for_container_selected = null;
        this.report_for_process_selected = null;
        this.report_container = null;
        this.report_process = null;
        this.container_selected = null;
      },
      deep: true
    },
    container_selected: function (new_value) {
      if (new_value == null) return;
      this.fetchReportContainer();
    },
    report_for_process_selected: function (new_value) {
      if (new_value == null) return;
      this.fetchReportProcess();
    }
  }
};
</script>
